import { mapGetters, mapState, mapActions, mapMutations } from 'vuex'
import { AccessPointsUtil } from '@/components/goals/common/access_points'
import AnchorPageLabel from '@/components/goals/AnchorPageLabel'
import PowerStandard from '@/components/goals/PowerStandard'
import BigGoal from '@/components/goals/BigGoal'
import GoalExampleContent from '@/components/goals/GoalExampleContent'
import Objective from '@/components/goals/Objective'
import ResourceListContainer from '@/components/ResourceListContainer'
import AccordionObjectivesList from '@/components/goals/AccordionObjectivesList'
import GoalContentPageStandards from '@/components/goals/GoalContentPageStandards'
import Staircase from '@/components/goals/Staircase'
import WikiText from '@/components/WikiText'
import CopyTrackWrapper from '@/components/CopyTrackWrapper'
import SubmitFeedback from '@/components/SubmitFeedback'
import { isSubjectLifeSkills, isSubjectELL } from '@/util/goals'

export {
  anchorPageMixin,
  goalPageMixin,
  saveCustomContentWithContentProfileMixin,
  trackGoalContentEditedMixin,
  trackAnchorContentEditedMixin,
  goalSetContentProfileMixin,
}

export { autoReviewMixin } from '@/components/goals/common/auto_review_mixin'

const saveCustomContentWithContentProfileMixin = {
  computed: {
    ...mapGetters('contentProfile', ['profileId'])
  },
  methods: {
    contentPayloadWithContentProfile(payload) {
      return Object.assign({}, payload, {
        content_profile_id: this.profileId
      })
    }
  }
}

const trackGoalContentEditedMixin = {
  methods: {
    ...mapActions('customgoal', ['initializeCustomGoal', 'goalTextEdited']),
    handleGoalTextEdited({ level, text }) {
      this.goalTextEdited({ level, text })
      this.$sendEvent('Edited Goal Text', {
        level,
        adapted_goal_level: level,
        text
      })
    }
  }
}

const trackAnchorContentEditedMixin = {
  methods: {
    ...mapMutations('customanchor', ['setObjective', 'setPrimaryGoalText']),
    annualTextEdited(text) {
      this.setPrimaryGoalText({ text })
      this.$sendEvent('Edited Goal Text', {
        obj_level: 'Annual',
        text
      })
    },
    objectiveTextEdited({ index, objective, allObjectives }) {
      this.setObjective({ index, objective })
      this.$sendEvent('Edited Goal Text', {
        obj_level: allObjectives ? 'all' : objective.level,
        text: objective.text
      })
    }
  }
}

const goalSetContentProfileMixin = {
  computed: {
    ...mapGetters('user', ['userContentProfileId']),
    ...mapState('contentProfile', ['fetchingActiveProfile'])
  },
  methods: {
    ...mapActions('contentProfile', ['setActiveProfileById'])
  }
}

const goalPageMixin = {
  components: {
    BigGoal,
    GoalExampleContent,
    GoalContentPageStandards,
    Staircase,
    WikiText,
    CopyTrackWrapper,
    SubmitFeedback,
  },
  props: {
    breadcrumbSubject: {
      type: String,
      default: ''
    },
    breadcrumbDomain: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      fetchingStandards: false,
      fetchingStaircase: false
    }
  },
  computed: {
    ...mapState('visibility', ['betaVisibility']),
    ...mapState('goaldetail', {
      pageContent: state => state.content,
      fetchingContent: state => state.fetching,
      staircaseContent: state => state.staircaseContent
    }),
    ...mapGetters('goaldetail', [
      'strategyMap',
      'visibleGroupedStandardsBySetCode',
      'accessInfo',
      'actionPermissions'
    ]),
    ...mapGetters('contentProfile', [
      'profileId',
      'visibleStandardSets',
      'staircaseStandardSetForSubject'
    ]),
    goalHeaderText() {
      if (isSubjectELL(this.pageContent.subject)) {
        return 'Advanced Level'
      }
      if (isSubjectLifeSkills(this.pageContent.subject)) {
        return 'Core Goal'
      }
      return 'Grade Level'
    },
  },
  methods: {
    ...mapActions('goaldetail', [
      'fetchPageContent',
      'fetchPageStandardsForSets',
      'fetchPageStaircaseContentForSet'
    ]),
    fetchPageStandardsAndStaircase() {
      if (this.pageContent.uuid && this.profileId) {
        // both the content and profile details have been retrieved
        if (this.visibleStandardSets && this.visibleStandardSets.length > 0) {
          this.fetchingStandards = true
          this.fetchPageStandardsForSets({
            standardSets: this.visibleStandardSets,
            contentStandardSetRefs: this.pageContent.standard_set_refs
          }).then(() => {
            this.fetchingStandards = false
          })
        }

        if (this.pageContent.subject) {
          let staircaseStandardSetCode = this.staircaseStandardSetForSubject(
            this.pageContent.subject
          )
          this.fetchingStaircase = true
          this.fetchPageStaircaseContentForSet({
            staircaseStandardSetCode,
            contentUuid: this.pageContent.uuid,
            subject: this.pageContent.subject,
          }).then(() => {
            this.fetchingStaircase = false
          })
        }
      }
    },
    clickedExampleContent(isContentRevealed) {
      this.expandFooter = !this.expandFooter
      this.$sendEvent('Clicked Example Content', { goal_example_shown: isContentRevealed })
    }
  },
  watch: {
    profileId() {
      this.fetchPageStandardsAndStaircase()
    },
    pageContent() {
      this.fetchPageStandardsAndStaircase()
    }
  }
}

const anchorPageMixin = {
  mixins: [goalPageMixin],
  components: {
    PowerStandard,
    AnchorPageLabel,
    Objective,
    ResourceListContainer,
    AccordionObjectivesList
  },
  data() {
    return {
      accessPoints: {
        [AccessPointsUtil.valuesMap.GRADE_LEVEL]: null,
        [AccessPointsUtil.valuesMap.APPROACHING]: null,
        [AccessPointsUtil.valuesMap.DEVELOPING]: null
      }
    }
  },
  computed: {
    ...mapGetters('user', ['interactiveGuidesOn']),
    ...mapGetters('contentProfile', ['visibleStandardSetsForSubject', 'keyStandardSetForSubject']),
    keyStandard() {
      let keyStandardSetCode = this.keyStandardSetForSubject(this.pageContent.subject)
      let standardsForSetCode = this.visibleGroupedStandardsBySetCode
        .filter(group => group.standardSetCode === keyStandardSetCode)
        .map(group => group.standards)[0]

      if (standardsForSetCode && standardsForSetCode[0]) {
        return standardsForSetCode[0]
      } else {
        // if content is not aligned to a standard in the key standard set, fallback to the first standard set in the ordered list that is tagged to the content
        return (
          this.visibleGroupedStandardsBySetCode[0] &&
          this.visibleGroupedStandardsBySetCode[0].standards &&
          this.visibleGroupedStandardsBySetCode[0].standards[0]
        )
      }
    },
    keyStandardArray() {
      return [this.keyStandard]
    },
    groupedSidebarStandards() {
      let keyStandard = this.keyStandard
      if (keyStandard) {
        return this.visibleGroupedStandardsBySetCode
          .map(group => {
            if (group.standardSetCode === keyStandard.standard_set_code) {
              group.standards = group.standards.filter(
                standard => standard.standard_long_ref !== keyStandard.standard_long_ref
              )
            }
            return group
          })
          .filter(group => group.standards.length > 0)
      } else {
        return this.visibleGroupedStandardsBySetCode
      }
    },
    hasPMAs() {
      return this.pageContent.goal_resources && this.pageContent.goal_resources.length > 0
    },
    hasObjectives() {
      return this.pageContent.objectives && this.pageContent.objectives.length > 0
    },
    hasAccordionObjectives() {
      return this.hasObjectives && (
        (!this.pageContent.objective_resources || !Object.keys(this.pageContent.objective_resources).length)
        && (!this.pageContent.assessment_items || !Object.keys(this.pageContent.assessment_items).length)
      )
    },
    accessPointsLevelUuidMap() {
      return Object.keys(this.accessPoints).reduce((map, level) => {
        map[level] = this.accessPoints[level] && this.accessPoints[level].uuid
        return map
      }, {})
    },
    areAccessPointsPublished() {
      let levelsPublished = AccessPointsUtil.orderedValues.reduce((map, level) => {
        map[level] = (this.accessPoints[level] && this.accessPoints[level].published) || false
        return map
      }, {})

      return Object.values(levelsPublished).every(published => published)
    },
    showAnnualGoalFooter() {
      return (
        !this.fetchingContent &&
        ((this.pageContent.goal_example_text && this.pageContent.goal_example_text.length > 0) ||
          (this.pageContent.goal_resources && this.pageContent.goal_resources.length > 0))
      )
    },
  },
  methods: {
    setAccessPointsForContent(pageContent) {
      if (pageContent.access_points && pageContent.access_points.length > 0) {
        this.accessPoints[pageContent.access_point_level] = Object.assign({}, pageContent)
        pageContent.access_points.forEach(apContent => {
          if (apContent.access_point_level) {
            this.accessPoints[apContent.access_point_level] = apContent
          }
        })
      } else {
        this.accessPoints = AccessPointsUtil.orderedValues.reduce((map, level) => {
          map[level] = null
          return map
        }, {})
      }
    },
    toggledGoalResources(isContentRevealed) {
      this.$sendEvent('Toggled Annual Resources', {
        annual_resources_shown: isContentRevealed
      })
    },
    toggledQuarterlyObjectives(isContentRevealed) {
      this.$sendEvent('Toggled Quarterly Objectives', {
        quarterly_objectives_shown: isContentRevealed
      })
    },
    toggledSDIStrategies(isContentRevealed) {
      this.$sendEvent('Toggled SDI Strategies', {
        sdi_strategies_shown: isContentRevealed
      })
    }
  }
}
